import {
  isSahServiceStructuralDriftMetadata,
  sahServiceStructuralDriftHref,
} from '@/lib/clients/sah-service-structural-drift';
import { leaveRequestResourceUrl } from '@/lib/workflow-routing/templates';
import { matchGeneratedProgramRoutePolicy } from '@statura/experience-contract';

export type NotificationProgramFamily = 'SAH' | 'RAC' | 'OTHER' | 'MIXED' | 'SHARED' | 'UNKNOWN';
export type NotificationDeliveryProgramFamily = Exclude<NotificationProgramFamily, 'UNKNOWN'>;

export interface NotificationInboxRow {
  id: string;
  title?: string | null;
  body?: string | null;
  type?: string | null;
  channel?: string | null;
  status?: string | null;
  read_at?: string | null;
  resource_type?: string | null;
  resource_id?: string | null;
  metadata?: Record<string, unknown> | null;
  created_at: string;
}

const FALLBACK_APP_URL = 'https://app.statura.care';

const RESOURCE_ROUTE_BUILDERS: Record<string, (id: string) => string> = {
  assessment: (id) => `/assessments/${id}`,
  care_plan: (id) => `/care-plans/${id}`,
  care_plans: (id) => `/care-plans/${id}`,
  client: (id) => `/clients/${id}`,
  // The reminder's task id is not a page; the renewal workflow lives on Government connections.
  government_credential_expiry: () => '/settings/integrations',
  incident: (id) => `/compliance/incidents/${id}`,
  incidents: (id) => `/compliance/incidents/${id}`,
  // Role-blind fallback: notification audit actions and historic rows without viewer context.
  leave_request: () => '/roster/leave',
  open_shift: (id) => `/roster/open-shifts/${id}`,
  open_shifts: (id) => `/roster/open-shifts/${id}`,
  medication: () => '/medications',
  medication_administrations: (id) => `/medications/mar?focus=${id}`,
  qi_collection_periods: (id) => `/compliance/quality-indicators?tab=quarter&focus=${id}`,
  shift: () => '/roster',
  roster_shift: (id) => `/roster/replace/${id}`,
  roster_shifts: (id) => `/roster/replace/${id}`,
  staff: (id) => `/people/workers/${id}`,
  timesheet: () => '/roster/timesheets',
  worker_certification: () => '/people/compliance/certifications',
  worker_certifications: () => '/people/compliance/certifications',
  worker_screening: (id) => `/people/compliance/screening/${id}`,
  worker_screenings: (id) => `/people/compliance/screening/${id}`,
  worker_onboarding_document: (id) => `/people/onboarding/documents/${id}`,
  worker_onboarding_documents: (id) => `/people/onboarding/documents/${id}`,
  worker_onboarding_journey: (id) => `/people/onboarding/${id}`,
  worker_onboarding_journeys: (id) => `/people/onboarding/${id}`,
  workflow_task: (id) => `/tasks/${id}`,
  workflow_tasks: (id) => `/tasks/${id}`,
};

export function notificationDeliveryProgramFamily(input: {
  resourcePath: string | null;
  resourceType: string | null;
}): NotificationDeliveryProgramFamily | null {
  const policy = input.resourcePath
    ? matchGeneratedProgramRoutePolicy('web', input.resourcePath)
    : null;
  if (policy?.classification === 'PROGRAM_NEUTRAL' && policy.sharedSemanticProofId) return 'SHARED';
  if (policy?.classification === 'SAH_ONLY') return 'SAH';
  if (policy?.classification === 'RAC_ONLY') return 'RAC';
  if (policy?.classification === 'OTHER_PROGRAM') return 'OTHER';
  if (input.resourceType === 'qi_collection_periods') return 'RAC';
  return null;
}

function appOrigin(appUrl?: string): string {
  try {
    return new URL(appUrl || process.env.NEXT_PUBLIC_APP_URL || FALLBACK_APP_URL).origin;
  } catch {
    return new URL(FALLBACK_APP_URL).origin;
  }
}

function hasControlCharacter(value: string): boolean {
  for (let index = 0; index < value.length; index += 1) {
    const code = value.charCodeAt(index);
    if (code <= 31 || code === 127) return true;
  }
  return false;
}

export function safeInternalNotificationPathFromUrl(
  value: unknown,
  appUrl?: string,
): string | null {
  if (typeof value !== 'string') return null;
  const raw = value.trim();
  if (!raw || hasControlCharacter(raw)) return null;
  if (raw.includes('\\')) return null;

  if (raw.startsWith('/') && !raw.startsWith('//')) return raw;
  if (!/^https?:\/\//i.test(raw)) return null;

  try {
    const parsed = new URL(raw);
    if (parsed.origin !== appOrigin(appUrl)) return null;
    return `${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch {
    return null;
  }
}

export function isNotificationUnread(row: Pick<NotificationInboxRow, 'read_at' | 'status'>) {
  if (row.read_at) return false;
  return String(row.status ?? '').toLowerCase() !== 'read';
}

function notificationClientId(metadata: Record<string, unknown> | null | undefined): string | null {
  const raw = metadata?.client_id ?? metadata?.clientId;
  return typeof raw === 'string' && raw.trim() ? raw.trim() : null;
}

/** Role-aware leave inbox path — use from surfaces that know the signed-in viewer's role. */
export function leaveRequestInboxPath(
  requestId: string,
  viewerRole: string | null | undefined,
  metadata?: Record<string, unknown> | null,
): string {
  return leaveRequestResourceUrl(requestId, metadata ?? undefined, viewerRole);
}

export function toSafeInternalNotificationPath(
  row: Pick<NotificationInboxRow, 'metadata' | 'resource_type' | 'resource_id'>,
  appUrl?: string,
  viewerRole?: string | null,
): string | null {
  if (isSahServiceStructuralDriftMetadata(row.metadata)) {
    return sahServiceStructuralDriftHref(notificationClientId(row.metadata));
  }

  const metadataPath = safeInternalNotificationPathFromUrl(row.metadata?.url, appUrl);
  const resourceType = row.resource_type?.toLowerCase() ?? '';
  // Production 2026-08-14/15 drift deliveries baked a generic service-delivery
  // URL. That is the same class as the audit fallback: not a usable owner
  // destination. Prefer the coordinator schedule route.
  if (isGenericServicesDeliveredInboxPath(metadataPath, resourceType)) {
    return sahServiceStructuralDriftHref(notificationClientId(row.metadata));
  }
  if (
    metadataPath &&
    !isNotificationAuditFallbackPath(metadataPath) &&
    !isRetiredNotificationInboxPath(metadataPath)
  ) {
    return metadataPath;
  }
  if (!row.resource_type || !row.resource_id) return null;

  if (resourceType === 'leave_request' && viewerRole !== undefined) {
    return leaveRequestInboxPath(row.resource_id, viewerRole, row.metadata);
  }

  const builder = RESOURCE_ROUTE_BUILDERS[resourceType];
  const resourcePath = builder ? builder(row.resource_id) : null;
  return (
    resourcePath ??
    (metadataPath && isRetiredNotificationInboxPath(metadataPath) ? null : metadataPath)
  );
}

function declaredNotificationProgramFamily(
  metadata: Record<string, unknown> | null | undefined,
): Exclude<NotificationProgramFamily, 'UNKNOWN'> | null {
  const raw = metadata?.program_family ?? metadata?.programFamily;
  return raw === 'SAH' || raw === 'RAC' || raw === 'OTHER' || raw === 'SHARED' ? raw : null;
}

/**
 * Presentation backstop for historic rows while the controlled inbox RPC
 * enforces the same selected-program boundary before returning bytes.
 */
export function isNotificationVisibleForProgram(
  row: Pick<NotificationInboxRow, 'metadata' | 'resource_type' | 'resource_id'>,
  programFamily: NotificationProgramFamily,
): boolean {
  const selected =
    programFamily === 'SAH' || programFamily === 'RAC' || programFamily === 'OTHER'
      ? programFamily
      : null;
  const path = toSafeInternalNotificationPath(row);
  const governed = notificationDeliveryProgramFamily({
    resourcePath: path,
    resourceType: row.resource_type ?? null,
  });

  // The generated route/resource policy outranks mutable delivery metadata.
  // This prevents a stale or malformed `program_family` stamp from relabelling
  // a known RAC destination as SAH, or vice versa.
  if (governed === 'SHARED') return true;
  if (governed) return selected !== null && governed === selected;

  const declared = declaredNotificationProgramFamily(row.metadata);
  return declared === 'SHARED' || (selected !== null && declared === selected);
}

function isNotificationAuditFallbackPath(path: string): boolean {
  return (
    path.startsWith('/settings/notification-audit') ||
    path.startsWith('/settings/notifications/log')
  );
}

// The web app has no /notifications page (the bell is the inbox); historic rows may
// still carry it. The care-worker app's /notifications screen is not resolved here.
function isRetiredNotificationInboxPath(path: string): boolean {
  return /^\/notifications(?:[/?#]|$)/.test(path);
}

function isGenericServicesDeliveredInboxPath(path: string | null, resourceType: string): boolean {
  return (
    resourceType === 'services_delivered' &&
    Boolean(path?.startsWith('/home-care/service-delivery'))
  );
}
